import { Image, Empty, Spin, Button } from "antd";
import { Eye, ImageIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { getChannelCustomerPhotos } from "../api/ChannelCustomer";

// Số ảnh hiển thị mỗi trang (phân trang phía client)
const PAGE_LIMIT = 6;

export default function CustomerImageGallery({ channelCustomerId }: { channelCustomerId?: number | null }) {
    const [images, setImages] = useState<{ id: number; url: string; title: string }[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);

    useEffect(() => {
        setPage(1);
        if (!channelCustomerId) {
            setImages([]);
            return;
        }
        let active = true;
        setLoading(true);
        getChannelCustomerPhotos(channelCustomerId, { page: 1, limit: PAGE_LIMIT })
            .then((res) => {
                if (!active) return;
                const mapped = (res.data || [])
                    .filter((p) => !!p.image_path)
                    .map((p, idx) => ({
                        id: p.id,
                        url: p.image_path,
                        title: `Khách gửi ${String(idx + 1).padStart(2, "0")}`,
                    }));
                setImages(mapped);
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => { active = false; };
    }, [channelCustomerId]);

    const visibleImages = useMemo(() => images.slice(0, page * PAGE_LIMIT), [images, page]);
    const remaining = images.length - visibleImages.length;

    return (
        <div className="bg-white dark:bg-[#1f1f1f] rounded-xl shadow-sm border border-slate-100 dark:border-[#2a2a2a] p-6">

            {/* Header */}
            <div className="
                flex
                flex-col
                sm:flex-row
                sm:items-center
                sm:justify-between
                gap-2
                mb-4
            ">
                <h3 className="
                    text-xs
                    sm:text-sm
                    font-bold
                    text-slate-400
                    uppercase
                    tracking-wider
                    flex
                    items-center
                    gap-2
                ">
                    <ImageIcon
                        size={16}
                        className="text-[#3e91ab]"
                    />
                    Hình ảnh khách gửi (Click để zoom)
                </h3>
                {images.length > 0 && (
                    <span className="text-xs text-slate-400">
                        {visibleImages.length}/{images.length}
                    </span>
                )}
            </div>

            {/* Gallery */}
            {loading ? (
                <div className="flex justify-center py-8">
                    <Spin size="small" />
                </div>
            ) : images.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có ảnh" />
            ) : (
                <Image.PreviewGroup>
                    <div className="
                        grid
                        grid-cols-2
                        sm:grid-cols-3
                        md:grid-cols-4
                        lg:grid-cols-4
                        gap-3
                    ">

                        {visibleImages.map((item) => (
                            <div
                                key={item.id}
                                className="
                                    relative
                                    rounded-xl
                                    overflow-hidden
                                    aspect-square
                                    bg-slate-100
                                    border
                                    border-slate-200
                                    dark:border-[#2a2a2a]
                                    shadow-sm
                                    group
                                    cursor-pointer
                                "
                            >
                                <Image
                                    src={item.url}
                                    alt={item.title}
                                    width="100%"
                                    height="100%"
                                    className="object-cover"
                                    preview={{
                                        mask: (
                                            <div className="flex items-center justify-center gap-2">
                                            </div>
                                        ),
                                    }}
                                />

                                {/* Hover overlay */}
                                <div
                                    className="
                                        absolute
                                        inset-0
                                        bg-black/40
                                        opacity-0
                                        group-hover:opacity-100
                                        transition
                                        duration-200
                                        flex
                                        items-end
                                        p-2.5
                                        pointer-events-none
                                    "
                                >
                                    <div className="flex items-center justify-between w-full text-white">
                                        <span className="text-xs font-bold truncate">
                                            {item.title}
                                        </span>

                                        <Eye
                                            size={14}
                                            className="flex-shrink-0"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}

                    </div>
                </Image.PreviewGroup>
            )}

            {remaining > 0 && (
                <div className="flex justify-center mt-3">
                    <Button size="small" type="link" onClick={() => setPage((p) => p + 1)}>
                        Xem thêm ({remaining})
                    </Button>
                </div>
            )}

            {/* Footer */}
            <p
                className="
                    text-xs
                    text-slate-400
                    mt-3
                    font-semibold
                    text-center
                    italic
                "
            >
                * Hỗ trợ định dạng JPG, PNG để tư vấn điều trị từ xa.
            </p>

        </div>
    );
}
